import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

const CATEGORIES = ["feedback", "bug", "help", "other"];

export default function FeedbackModal({ open, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [category, setCategory] = useState("feedback");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        email: email || null,
        message,
        category,
      });
      toast.success(t("feedback_thanks"));
      setMessage("");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="feedback-modal"
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
      style={{ zIndex: 10001 }}
      onClick={onClose}
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6 border border-[#E6E2D8]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-[#143122]">{t("get_in_touch")}</div>
            <h3 className="mt-1 font-serif-display text-2xl text-[#143122]">{t("leave_feedback")}</h3>
            <p className="text-xs text-[#1A1F1C]/60 mt-1">{t("feedback_sub")}</p>
          </div>
          <button data-testid="feedback-modal-close" onClick={onClose} className="text-[#1A1F1C]/60 hover:text-[#143122]">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5">{t("feedback_category")}</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`fb-cat-${c}`}
                  onClick={() => setCategory(c)}
                  className={`kav-chip ${category === c ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                >
                  {t(`fb_${c}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">{t("email")} <span className="text-[#1A1F1C]/40">({t("optional")})</span></label>
            <input data-testid="feedback-email" type="email" className="kav-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5">{t("message")}</label>
            <textarea data-testid="feedback-message" required rows={5} className="kav-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("feedback_placeholder")} />
          </div>
          <button data-testid="feedback-submit-btn" type="submit" disabled={busy} className="kav-btn-primary w-full justify-center">
            {busy ? "..." : t("send_feedback")}
          </button>
        </form>
      </div>
    </div>
  );
}
